/**
 * Shared helpers for the Rebuy widget callbacks
 * (rebuy/gwp-callback-products-change.js, rebuy/sample-callback-products-change.js,
 * rebuy/widget-callback-products-change.js).
 * Loaded globally so those dashboard-pasted callbacks can call into it as window.RebuySharedHelpers.
 */
const READY_INITIAL_DELAY_MS = 500;
const READY_RETRY_DELAY_MS = 300;
const READY_MAX_ATTEMPTS = 5;

function hasItemInCart(id) {
  return Rebuy.SmartCart.items().some((item) => item.properties?._widget_id === id);
}

/**
 * Injects a stock delivery status message (available / continue / sold out) into a
 * product block, matching the same states and classes used in the cart drawer's
 * rebuy-cart-items.vue, driven by window.stockDeliveryTimes.
 * @param {string[]} anchorSelectors - tried in order to find the element the status is inserted after.
 */
function addInventoryStatus(infoEl, product, anchorSelectors) {
  if (infoEl.querySelector('.rebuy-cart__flyout-item-inventory-status')) return;

  const variant = product?.selected_variant || product?.variants?.[0];
  if (!variant) return;

  const quantity = variant.inventory_quantity;
  const policy = variant.inventory_policy;

  let modifier;
  let text;
  if (quantity > 0) {
    modifier = 'available';
    text = window.stockDeliveryTimes?.available || 'Available';
  } else if (policy === 'continue') {
    modifier = 'continue';
    text = `${quantity} ${window.stockDeliveryTimes?.continue || 'Continue'}`;
  } else {
    modifier = 'sold-out';
    text = `${quantity} ${window.stockDeliveryTimes?.soldOut || 'Sold out'}`;
  }

  const statusEl = document.createElement('p');
  statusEl.className = `rebuy-cart__flyout-item-inventory-status rebuy-cart__flyout-item-inventory-status--${modifier}`;
  statusEl.textContent = text;

  const anchorEl = anchorSelectors.reduce(
    (found, selector) => found || infoEl.querySelector(selector),
    null
  );
  (anchorEl || infoEl).insertAdjacentElement('afterend', statusEl);
}

/**
 * Injects a pipe-separated ingredients message into a product block, sourced from the
 * `metafields.details.ingredients` list metafield (e.g. size/price variants for hair dye).
 */
function addIngredientMessage(infoEl, product) {
  if (infoEl.querySelector('.rebuy-cart__flyout-item-ingredients')) return;

  const ingredients = product?.metafields?.details?.ingredients?.value;
  if (!Array.isArray(ingredients) || ingredients.length === 0) return;

  const messageEl = document.createElement('p');
  messageEl.className = 'rebuy-cart__flyout-item-ingredients';
  messageEl.textContent = ingredients.join(' | ');
  infoEl.insertAdjacentElement('beforeend', messageEl);
}

/**
 * Fetches a variant from Shopify's Ajax product endpoint, memoised per variant id.
 * Rebuy re-renders the widget's product blocks on every cart change, so without this the
 * same request is re-issued each time and the unit price line only lands once the network
 * round trip resolves — growing the block after it has already painted, which shifts
 * everything below it. A resolved cache entry settles within a microtask instead.
 */
function fetchVariant(handle, variantId) {
  const cached = window.RebuySharedHelpers.variantCache.get(variantId);
  if (cached) return cached;

  const request = fetch(`/products/${handle}.js`)
    .then((response) => response.json())
    .then((fetchedProduct) => fetchedProduct?.variants?.find((v) => v.id === variantId) || null)
    .catch(() => null);

  window.RebuySharedHelpers.variantCache.set(variantId, request);
  return request;
}

/**
 * Fetches a product's variants via Shopify's Ajax product endpoint (widget.data.products
 * mirrors the Admin REST resource, which never carries unit_price_measurement) and injects
 * the unit price line after `.rebuy-product-price`, matching rebuy-cart-items.vue's format.
 */
async function addUnitPriceMeasurement(infoEl, product) {
  const priceEl = infoEl.querySelector('.rebuy-product-price');
  if (!priceEl || !product?.handle || infoEl.querySelector('.rebuy-cart__flyout-item-unit-price')) {
    return;
  }

  const variant = product?.selected_variant || product?.variants?.[0];
  if (!variant?.id) return;

  const fetchedVariant = await fetchVariant(product.handle, variant.id);
  const unitPriceMeasurement = fetchedVariant?.unit_price_measurement;

  // The block can be re-rendered or re-processed while the first fetch is still in flight.
  if (infoEl.querySelector('.rebuy-cart__flyout-item-unit-price')) return;

  if (!unitPriceMeasurement || typeof fetchedVariant.unit_price !== 'number') return;
  const unitPriceEl = document.createElement('p');
  unitPriceEl.className = 'rebuy-cart__flyout-item-unit-price';
  unitPriceEl.textContent = `${Math.round(unitPriceMeasurement.quantity_value)}${unitPriceMeasurement.quantity_unit} (${Rebuy.Cart.formatMoney(fetchedVariant.unit_price)} / ${unitPriceMeasurement.reference_value}${unitPriceMeasurement.reference_unit})`;
  priceEl.insertAdjacentElement('afterend', unitPriceEl);
}

function syncLockedState(guardName, guard) {
  document.querySelectorAll('[id^="rebuy-widget-"]').forEach((widgetEl) => {
    const id = widgetEl.id.replace('rebuy-widget-', '');
    if (!guard.widgetIds.has(id)) return;

    widgetEl.classList.toggle('gwp-locked', guard.lockedIds.has(id));
  });
}

/**
 * Creates (or returns the existing) click-guard state for a widget family, keyed by name
 * (e.g. 'gwp', 'sample') so different widget types don't share lock state. Installs the
 * click-guard listener and cart-change listener once per name; every call refreshes
 * `guard.onCartChange` so the listener always runs the latest callback's closure (each
 * "products change" callback invocation gets a fresh `widget` reference from Rebuy, and
 * the old callback's `widget` snapshot goes stale as soon as the product list changes).
 * @param {Function} [options.onCartChange] - extra logic to run after each cart-change sync.
 */
function createWidgetGuard(name, { onCartChange } = {}) {
  if (!window.RebuySharedHelpers.guards) {
    window.RebuySharedHelpers.guards = {};
  }

  const existingGuard = window.RebuySharedHelpers.guards[name];
  if (existingGuard) {
    existingGuard.onCartChange = onCartChange;
    return existingGuard;
  }

  const guard = { widgetIds: new Set(), lockedIds: new Set(), onCartChange };
  window.RebuySharedHelpers.guards[name] = guard;

  // Capture phase so Rebuy's own handler never runs once locked
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('.rebuy-product-block .rebuy-button');
      if (!button) return;

      const widgetEl = button.closest('[id^="rebuy-widget-"]');
      if (!widgetEl) return;

      const clickedId = widgetEl.id.replace('rebuy-widget-', '');
      if (!guard.widgetIds.has(clickedId)) return;

      if (guard.lockedIds.has(clickedId)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      guard.lockedIds.add(clickedId);
      widgetEl.classList.add('gwp-locked');
    },
    true
  );

  document.addEventListener('rebuy:cart.change', () => {
    guard.widgetIds.forEach((id) => {
      if (hasItemInCart(id)) {
        guard.lockedIds.add(id);
      } else {
        guard.lockedIds.delete(id);
      }
    });
    syncLockedState(name, guard);
    guard.onCartChange?.();
  });

  return guard;
}

/**
 * Registers a widget with its guard and syncs its initial locked state, replacing the
 * repeated bottom block in each callback file.
 */
function initWidgetLock(guard, widgetId) {
  guard.widgetIds.add(widgetId);

  if (hasItemInCart(widgetId)) {
    guard.lockedIds.add(widgetId);
  } else {
    guard.lockedIds.delete(widgetId);
  }

  const widgetEl = document.querySelector(`#rebuy-widget-${widgetId}`);
  if (widgetEl) {
    widgetEl.classList.toggle('gwp-locked', guard.lockedIds.has(widgetId));
  }
}

/**
 * Whether Rebuy's cart data has actually hydrated. `Rebuy.SmartCart.items()` alone can't
 * tell "cart is empty" apart from "cart hasn't loaded", so the hydrated cart snapshot
 * (the same object the sample "on ready" callback reads `total_price` from) is the signal.
 */
function isCartReady() {
  return (
    typeof window.Rebuy?.SmartCart?.items === 'function' &&
    Array.isArray(window.Rebuy?.Cart?.cart?.items)
  );
}

/**
 * Runs `run` as soon as `isReady` returns true, retrying on the same cadence the sample
 * "on ready" callback uses, because Rebuy's cart data and widget markup both lag briefly
 * behind the callbacks that depend on them. Chains are token-guarded per `key`, so a newer
 * invocation abandons an in-flight chain instead of the two racing each other.
 * @param {string} key - Identifies the chain (e.g. `sample-exclude-<widgetId>`).
 * @param {Function} isReady - Predicate re-evaluated on every attempt.
 * @param {Function} run - Called once, on the first attempt where `isReady()` is true.
 */
function runWhenReady(key, isReady, run) {
  window.RebuySharedHelpers.readyTokens ??= {};
  const token = (window.RebuySharedHelpers.readyTokens[key] ?? 0) + 1;
  window.RebuySharedHelpers.readyTokens[key] = token;

  const attempt = (count) => {
    // A newer chain for this key took over; abandon this one.
    if (window.RebuySharedHelpers.readyTokens[key] !== token) return;

    if (isReady()) {
      run();
      return;
    }

    if (count >= READY_MAX_ATTEMPTS) return;

    const delay = count === 0 ? READY_INITIAL_DELAY_MS : READY_RETRY_DELAY_MS;
    setTimeout(() => attempt(count + 1), delay);
  };

  attempt(0);
}

window.RebuySharedHelpers = {
  hasItemInCart,
  addInventoryStatus,
  addIngredientMessage,
  addUnitPriceMeasurement,
  syncLockedState,
  createWidgetGuard,
  initWidgetLock,
  isCartReady,
  runWhenReady,
  variantCache: new Map(),
};
